import React, { useState, useRef, useEffect } from 'react';
import { getVideoLocalSrc } from './videoUtility';
import { getImageHandlerSSR } from '../Image/utility';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
interface DynamicVideoProps {
  builderMode?: boolean;
  videoUrl: string;
  placeholderUrl: string;
  mediaMap?: Map<string, any>;
  altText?: string;
  style?: React.CSSProperties;
  lazyLoadPlaceholder?: boolean;
  width?: number | string;
  height?: number | string;
  onLoad?: () => void;
  onError?: () => void;
  hideControls?: boolean;
  [key: string]: any;
}

const visuallyHidden = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
} as const;

const Video: React.FC<DynamicVideoProps> = ({
  builderMode = false,
  videoUrl,
  placeholderUrl,
  mediaMap,
  lazyLoadPlaceholder = true,
  altText = '',
  style = {},
  width,
  height,
  onLoad,
  onError,
  hideControls = false,
  ...props
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  // Resolve media URLs
  const videoLocalSrc = getVideoLocalSrc({
    builderMode,
    videoUrl,
    videoMap: mediaMap,
  });

  const { currentSrc: placeholderLocalSrc, fallbackUrl } = getImageHandlerSSR({
    builderMode,
    imageUrl: placeholderUrl,
    MediaMap: mediaMap,
  });

  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [isPlaying, setIsPlaying] = useState(true);
  const [muted, setMuted] = useState(true);

  const handleVideoLoad = () => {
    setIsVideoLoaded(true);
    if (onLoad) onLoad();
  };

  const handleVideoError = () => {
    setIsVideoLoaded(false);
    if (onError) onError();
  };

  // Check if video is already ready (cached or preloaded)
  useEffect(() => {
    if (!builderMode && mediaMap && videoRef.current) {
      const video = videoRef.current;
      // readyState >= 3 means HAVE_FUTURE_DATA or HAVE_ENOUGH_DATA
      if (video.readyState >= 3) {
        handleVideoLoad();
      }
    }
  }, [builderMode, mediaMap]);

  const togglePlay = () => {
    if (!videoRef.current) return;

    if (isPlaying) {
      videoRef.current.pause();
      setIsPlaying(false);
    } else {
      videoRef.current.play();
      setIsPlaying(true);
    }
  };

  const toggleMute = () => {
    if (!videoRef.current) return;

    videoRef.current.muted = !muted;
    setMuted(!muted);
  };

  // Builder mode fallback (no optimization)
  if (builderMode || !mediaMap) {
    return (
      <div style={{ position: 'relative', width: '100%', height: '100%', ...style }}>
        <video
          ref={videoRef}
          src={videoUrl}
          poster={placeholderUrl}
          muted
          autoPlay
          playsInline
          loop
          preload="auto"
          disablePictureInPicture
          controlsList="nodownload noremoteplayback nofullscreen noplaybackrate"
          onLoadedData={handleVideoLoad}
          onCanPlay={handleVideoLoad}
          onError={handleVideoError}
          onContextMenu={(e) => e.preventDefault()}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          {...props}
          controls={false}
        />

        {!hideControls && (
          <Controls isPlaying={isPlaying} muted={muted} togglePlay={togglePlay} toggleMute={toggleMute} />
        )}
      </div>
    );
  }

  // Normal optimized mode
  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', ...style }}>
      {/* Placeholder until video loads */}
      {!isVideoLoaded && (placeholderLocalSrc || fallbackUrl) && (
        <img
          loading={lazyLoadPlaceholder ? 'lazy' : 'eager'}
          src={isImageLoaded ? placeholderLocalSrc : fallbackUrl || placeholderLocalSrc}
          alt={altText}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            // Simple fade-in effect if switching from blur (fallback) to full
            transition: 'opacity 0.3s ease-in-out',
          }}
          onLoad={() => setIsImageLoaded(true)}
        />
      )}

      {/* Video element (NO native controls) */}
      <video
        ref={videoRef}
        src={videoLocalSrc}
        muted={muted}
        autoPlay
        playsInline
        loop
        preload="auto"
        disablePictureInPicture
        controlsList="nodownload noremoteplayback nofullscreen noplaybackrate"
        onLoadedData={handleVideoLoad}
        onCanPlay={handleVideoLoad}
        onError={handleVideoError}
        onContextMenu={(e) => e.preventDefault()}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: isVideoLoaded ? 'block' : 'none',
        }}
        {...props}
        controls={false}
      >
        <source src={videoLocalSrc} />
      </video>

      {/* Custom controls */}
      {!hideControls && (
        <Controls isPlaying={isPlaying} muted={muted} togglePlay={togglePlay} toggleMute={toggleMute} />
      )}
    </div>
  );
};

export default Video;

/* ------------------------
   Custom Controls
   ------------------------ */
const Controls = ({
  isPlaying,
  muted,
  togglePlay,
  toggleMute,
}: {
  isPlaying: boolean;
  muted: boolean;
  togglePlay: () => void;
  toggleMute: () => void;
}) => {
  return (
    <div
      style={{
        position: 'absolute',
        left: 8,
        bottom: 8,
        zIndex: 50,
        display: 'flex',
        gap: '0px',
        padding: '8px',
        borderRadius: '40px',
        // background: 'rgba(0,0,0,0.45)',
        // backdropFilter: 'blur(6px)',
      }}
    >
      {/* Play / Pause */}
      <button style={btnStyle} onClick={togglePlay} role="button" aria-label="Play/Pause">
        {isPlaying ? <PauseIcon sx={{ fontSize: '48px' }} /> : <PlayArrowIcon sx={{ fontSize: '48px' }} />}
        <span style={visuallyHidden}>Play/Pause</span>
      </button>

      {/* Mute / Unmute */}
      <button style={btnStyle} onClick={toggleMute} role="button" aria-label="Mute/Unmute">
        {muted ? <VolumeOffIcon sx={{ fontSize: '48px' }} /> : <VolumeUpIcon sx={{ fontSize: '48px' }} />}
        <span style={visuallyHidden}>Mute/Unmute</span>
      </button>
    </div>
  );
};

const btnStyle: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  color: 'white',
  fontSize: '22px',
};
